/**
 * Dedicated filter option queries for the mobile filter sheet.
 *
 * Mirrors the option-fetching logic from the web's `useFilterState` hook:
 * positions, leagues, clubs (with server-side search + label cache), and
 * nationalities all come from dedicated GraphQL queries rather than being
 * derived from loaded player rows.
 * @module features/scouting/hooks/useMobileFilterOptions
 */

import { useLayoutEffect, useMemo, useState } from "react";

import type { FilterOption } from "@/types/filter";

import {
  useRosterManagementListMyOrganizationsEligibleTeamsQuery,
  useRosterManagementListNationalitiesQuery,
  useRosterManagementListPositionsQuery,
} from "@/generated/graphql";
import { POSITIONS_FULL_LIST_FIRST } from "@/config/constants";
import useOrganizationLeagues from "@/hooks/useOrganizationLeagues";
import dedupePositionNodes from "@/utils/dedupePositionNodes";
import {
  buildNationalityMemberNamesById,
  groupNationalitiesByCountry,
} from "@/utils/nationalityCountryGroups";

/** Matches labels with at least one Unicode letter or digit (filters out invisible/whitespace-only names) */
const HAS_VISIBLE_TEXT = /[\p{L}\p{N}]/u;

/** Input parameters for the hook */
interface UseMobileFilterOptionsParams {
  /** Whether the user has authenticated (gates all queries via `skip`) */
  readonly hasAuth: boolean;
  /** Currently selected club IDs — used to preserve labels across searches */
  readonly selectedClubIds: readonly string[];
}

/** Return value of the hook */
interface UseMobileFilterOptionsResult {
  /** Position filter options from dedicated query */
  readonly positionOptions: readonly FilterOption[];
  /** League filter options from org-scoped query */
  readonly leagueOptions: readonly FilterOption[];
  /** Club filter options (server-side search, with label cache) */
  readonly clubOptions: readonly FilterOption[];
  /** Nationality filter options from dedicated query — one per country (SE-8325) */
  readonly nationalityOptions: readonly FilterOption[];
  /**
   * Every underlying `Nationality` entity name a selected country id should
   * match, keyed by that id — expands a single country selection to also
   * match dual-national players (SE-8325).
   */
  readonly nationalityMemberNamesById: Readonly<
    Record<string, readonly string[]>
  >;
  /** Current club search text */
  readonly clubSearchText: string;
  /** Update club search text (triggers server-side re-query) */
  readonly setClubSearchText: (text: string) => void;
  /** Whether the club search query is currently loading */
  readonly clubSearching: boolean;
}

/**
 * Fetches filter options from dedicated queries for mobile filter sheets.
 * @param params - Hook parameters
 * @param params.hasAuth - Whether the user is authenticated
 * @param params.selectedClubIds - Currently selected club IDs
 * @returns Filter options, club search state, and club search callback
 */
const useMobileFilterOptions = ({
  hasAuth,
  selectedClubIds,
}: UseMobileFilterOptionsParams): UseMobileFilterOptionsResult => {
  /* --- Position options --- */
  // `$first` must cover the FULL taxonomy: when omitted the backend returns
  // an arbitrary 20-row default page — on staging Goalkeeper is row 21 and
  // was silently missing from the filter sheet (SE-5481).
  const { data: positionData } = useRosterManagementListPositionsQuery({
    variables: { first: POSITIONS_FULL_LIST_FIRST },
    skip: !hasAuth,
  });
  const positionOptions = useMemo(
    (): readonly FilterOption[] =>
      dedupePositionNodes(positionData?.listPositions?.edges).map(node => ({
        id: node.id,
        label: node.name,
      })),
    [positionData?.listPositions?.edges]
  );

  /* --- League options --- */
  const { leagueOptions } = useOrganizationLeagues({ skip: !hasAuth });

  /* --- Nationality options --- */
  const { data: nationalitiesData } = useRosterManagementListNationalitiesQuery(
    {
      variables: { first: 1000 },
      skip: !hasAuth,
    }
  );
  const nationalityGroups = useMemo(
    () =>
      groupNationalitiesByCountry(
        (nationalitiesData?.listNationalities?.edges ?? []).map(e => e.node)
      ),
    [nationalitiesData?.listNationalities?.edges]
  );
  const nationalityOptions = useMemo(
    (): readonly FilterOption[] =>
      nationalityGroups.map(g => ({ id: g.id, label: g.label })),
    [nationalityGroups]
  );
  const nationalityMemberNamesById = useMemo(
    () => buildNationalityMemberNamesById(nationalityGroups),
    [nationalityGroups]
  );

  /* --- Club options with server-side search + label cache --- */
  const [clubSearchText, setClubSearchText] = useState("");
  const { data: teamsData, loading: clubSearching } =
    useRosterManagementListMyOrganizationsEligibleTeamsQuery({
      variables: { first: 50, search: clubSearchText || undefined },
      skip: !hasAuth,
    });

  const rawClubOptions = useMemo(
    (): readonly FilterOption[] =>
      [...(teamsData?.listMyOrganizationsEligibleTeams?.edges ?? [])]
        .map(e => ({ id: e.node.id, label: e.node.name }))
        .filter(o => o.id && HAS_VISIBLE_TEXT.test(o.label))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teamsData?.listMyOrganizationsEligibleTeams?.edges]
  );

  const [clubLabelCache, setClubLabelCache] = useState<Record<string, string>>(
    {}
  );

  const clubOptions = useMemo((): readonly FilterOption[] => {
    const currentIds = new Set(rawClubOptions.map(o => o.id));
    const cachedEntries = selectedClubIds
      .filter(id => !currentIds.has(id))
      .map(id => ({ id, label: clubLabelCache[id] ?? id }))
      .filter(o => o.label.trim());
    return cachedEntries.length > 0
      ? [...rawClubOptions, ...cachedEntries].sort((a, b) =>
          a.label.localeCompare(b.label)
        )
      : rawClubOptions;
  }, [rawClubOptions, selectedClubIds, clubLabelCache]);

  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Accumulate labels from paginated club search for stable chip display
    setClubLabelCache(prev =>
      rawClubOptions.reduce(
        (acc, o) => (acc[o.id] === o.label ? acc : { ...acc, [o.id]: o.label }),
        prev
      )
    );
  }, [rawClubOptions]);

  return {
    positionOptions,
    leagueOptions,
    clubOptions,
    nationalityOptions,
    nationalityMemberNamesById,
    clubSearchText,
    setClubSearchText,
    clubSearching,
  };
};

export default useMobileFilterOptions;
